/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { getRateLimitConfig } from './config';
import { isDeveloperEnvironment } from './environment';
import { hasRateLimitInterceptor } from './registerRateLimitInterceptor';

/**
 * How many store-driven notices are currently mounted. Module-level because the
 * point is to catch two of them in the same app, which no single instance can see.
 */
let mountedCount = 0;

const warnings = {
    inert:
        'The rate-limit notice is mounted but nothing can raise it, so a 429 will ' +
        'show nothing. Call registerRateLimitInterceptor() at startup — including ' +
        'for every axios.create() instance, which does not inherit interceptors. ' +
        'If this service detects 429s itself and calls reportRateLimit, say so with ' +
        'configureRateLimit({ hasOwnDetection: true }); if it wants no notice, pass ' +
        'mountRateLimitNotice={false} to ExternalAppProviders. For docs see: ' +
        'https://github.com/FootprintRecreation/r1s-frontend/blob/dev/shared-ui/rate-limit/README.md',
    duplicate:
        'A second rate-limit notice mounted, so a 429 will stack two dialogs. ' +
        'ExternalAppProviders mounts one already — drop the extra <RateLimitModal />, ' +
        'or pass mountRateLimitNotice={false} if this service means to mount its own. ' +
        'Both instances still render, since dropping this one would hide whatever ' +
        'configuration it carries. For docs see: ' +
        'https://github.com/FootprintRecreation/r1s-frontend/blob/dev/shared-ui/rate-limit/README.md',
};

/**
 * Warns a developer about the two ways the notice ends up misconfigured, both of
 * which are silent at runtime.
 *
 * The notice auto-mounts for services on `ExternalAppProviders` while registration
 * stays a deliberate act by the owning team, so a service can inherit a notice
 * nothing will ever raise — someone testing a 429 then sees nothing and cannot tell
 * that from having forgotten to register. The other direction is a service that
 * mounts its own notice on top of the inherited one and ships two stacked dialogs.
 */
export function useRateLimitMountWarnings() {
    React.useEffect(() => {
        mountedCount += 1;

        if (isDeveloperEnvironment()) {
            if (mountedCount > 1) {
                // eslint-disable-next-line no-console
                console.warn(warnings.duplicate);
            } else if (
                !hasRateLimitInterceptor() &&
                !getRateLimitConfig().hasOwnDetection
            ) {
                // eslint-disable-next-line no-console
                console.warn(warnings.inert);
            }
        }

        return () => {
            mountedCount -= 1;
        };
    }, []);
}

/**
 * Internal. Reached by tests through the module's `testing` entry point, like the
 * rest of the module's state. Unmounting keeps the count honest on its own, but a
 * test that renders without unmounting would otherwise poison every later one into
 * reporting a duplicate.
 */
export function resetRateLimitMountCount() {
    mountedCount = 0;
}
